/**
 * 63.1u, smoke dispensers on vehicles (GameConfig.vehicleSmoke: charges per vehicle, no reload; absent or 0, none): a pop
 * fires one of 65.15's smoke grenades from each barrel of the vehicle's launchers (67.77: SMOKE_LAUNCHER_FITS, "2 3 barrels"
 * at the hull's front, fanned from ahead to abeam), each from its muzzle along its barrel, landing a few people out by its
 * own arc, bounce and fuse — so the clouds wrap the vehicle's front and flanks. The driver pops one (an input, the human's
 * smoke key while driving); the bots pop under rocket pressure, hits beyond bullets or on a contested approach
 * (ai/actionBot.ts). Pure; the grenades are ordinary smoke grenades from then on.
 */
import type { GameState, GrenadeState, PlayerState, SmokeLauncherFit, StructureState, Vec3, VehicleKind } from '../types';
import { VEHICLE_SMOKE, SMOKE_LAUNCHER, SMOKE_LAUNCHER_FITS, GRENADES, PERSON_BODY_HEIGHT, PHYS_DT, TANK } from '../constants';
import { vecAdd, vecScale } from '../math';
import { dcos, dsin } from './dmath';
import { personSetOf } from './scaleProfile';
import { throwSpeed } from './grenades';
import { vehicleFrame } from './vehicleFrame';

/** 67.77: a vehicle's smoke launchers — its kind's default fit (none for a kind without one: the bike, a non-vehicle). */
export function smokeFitOf(vehicle: Pick<StructureState, 'type'>): SmokeLauncherFit {
  return SMOKE_LAUNCHER_FITS[vehicle.type as VehicleKind] ?? [];
}

/** One barrel's muzzle and the way it points, in people in the hull's frame (x left, y up, z front). */
export interface SmokeMuzzle { at: Vec3; dir: Vec3 }

/**
 * 67.77: a fit's muzzles, barrel by barrel (each mount's barrels in their row, its lowest yaw first): each barrel stands on
 * its block, its row across the mount's facing, fanned SMOKE_LAUNCHER.spreadDegrees from its neighbour and raised
 * elevationDegrees; the muzzle its length along it. The drawn launcher (rendering/smokeLauncher.ts) is built to the same.
 */
export function smokeMuzzles(fit: SmokeLauncherFit): SmokeMuzzle[] {
  const L = SMOKE_LAUNCHER, k = 1 / L.modelPerson, rad = Math.PI / 180;
  const ce = dcos(L.elevationDegrees * rad), se = dsin(L.elevationDegrees * rad);
  const out: SmokeMuzzle[] = [];
  for (const m of fit) {
    const cy = dcos(m.yaw * rad), sy = dsin(m.yaw * rad), side = Math.sign(m.yaw) || 1;
    for (let i = 0; i < m.barrels; i++) {
      const off = (i - (m.barrels - 1) / 2) * side;   // (toward the mount's outer side the barrel faces further out)
      const yaw = (m.yaw + off * L.spreadDegrees) * rad;
      const dir = { x: dsin(yaw) * ce, y: se, z: dcos(yaw) * ce };
      const dx = off * L.pitch;   // the row across the facing: local x turned by the mount's yaw
      const base = { x: m.at[0] + dx * cy, y: m.at[1] + L.blockHeight, z: m.at[2] - dx * sy };
      out.push({ at: vecScale(vecAdd(base, vecScale(dir, L.barrelLength)), k), dir });
    }
  }
  return out;
}

/**
 * 249's follow-up: a muzzle on a turret turned by its yaw about the turret's axis (`pivotZ` people ahead of the hull's foot)
 * — the hit shape's rule (core/hits.ts): the turret's front toward the hull's left by the yaw, as the drawn turret turns.
 */
export function turnedMuzzle(mz: SmokeMuzzle, yaw: number, pivotZ: number): SmokeMuzzle {
  if (!yaw) return mz;
  const c = dcos(yaw), s = dsin(yaw);
  const turn = (v: Vec3, z0: number): Vec3 => ({ x: v.x * c + (v.z - z0) * s, y: v.y, z: z0 - v.x * s + (v.z - z0) * c });
  return { at: turn(mz.at, pivotZ), dir: turn(mz.dir, 0) };
}

/** The charges a vehicle has left (none without the dial). */
export function smokeLeft(state: Pick<GameState, 'config'>, s: Pick<StructureState, 'smokeUsed'>): number {
  return Math.max(0, (state.config.vehicleSmoke ?? 0) - (s.smokeUsed ?? 0));
}

export type SmokeCheck = { ok: true; vehicle: StructureState } | { ok: false; reason: string; code: string };

/** May `unit` pop its vehicle's smoke at `tick`? It must drive it, the vehicle must stand, have a charge left, and be ready. */
export function canPopSmoke(state: GameState, unit: PlayerState, tick: number): SmokeCheck {
  if (!unit.alive || !unit.mount) return { ok: false, reason: 'Not in a vehicle', code: 'not mounted' };
  const v = state.structures.find(s => s.id === unit.mount!.bodyId);
  if (!v || v.health <= 0) return { ok: false, reason: 'No vehicle', code: 'vehicle' };
  if (v.seats?.[unit.mount.seat]?.role !== 'driver') return { ok: false, reason: 'Only the driver pops smoke', code: 'driver' };
  if (smokeFitOf(v).length === 0) return { ok: false, reason: 'No smoke launchers', code: 'launchers' };   // (67.77: the bike)
  if (smokeLeft(state, v) <= 0) return { ok: false, reason: 'No smoke charges left', code: 'none' };
  if ((v.smokeReady ?? 0) > tick) return { ok: false, reason: 'Smoke not ready', code: 'ready' };
  return { ok: true, vehicle: v };
}

/**
 * The pop: a grenade from each barrel's muzzle along its barrel (67.77), one charge spent, the next ready
 * VEHICLE_SMOKE.readySeconds on. The muzzles are placed in the vehicle's frame on the ground (core/vehicleFrame.ts, the frame
 * its hit shape and its drawn hull share). The launchers are fixed to the hull, but a tank's ride its turret (249: on its
 * flanks), turned by its yaw (`turnedMuzzle`). `toward` (the bots' threat direction) is accepted and not read.
 */
export function popVehicleSmoke(state: GameState, unit: PlayerState, vehicle: StructureState, tick: number, toward?: Vec3): GameState {
  void toward;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const f = vehicleFrame(state.planets[vehicle.planetIndex], vehicle, person, ps);
  const inWorld = (v: Vec3) => vecAdd(vecAdd(vecScale(f.left, v.x), vecScale(f.up, v.y)), vecScale(f.front, v.z));
  let id = (state.grenades ?? []).reduce((m, x) => Math.max(m, x.id + 1), state.grenadeSeq ?? 0);
  const yaw = vehicle.type === 'tank' ? vehicle.turretYaw ?? 0 : 0;
  const thrown: GrenadeState[] = smokeMuzzles(smokeFitOf(vehicle)).map(m => turnedMuzzle(m, yaw, TANK.turretPivotZ)).map(mz => {
    const pos = vecAdd(f.foot, vecScale(inWorld(mz.at), person));
    const speed = throwSpeed(state, pos) * Math.sqrt(VEHICLE_SMOKE.landPeople / GRENADES.rangePeople);
    return { id: id++, ownerId: unit.id, teamIndex: unit.teamIndex, kind: 'smoke', planetIndex: vehicle.planetIndex, pos, vel: vecScale(inWorld(mz.dir), speed), fuseLeft: GRENADES.smoke.fuseSeconds };
  });
  return {
    ...state,
    grenadeSeq: id,
    grenades: [...(state.grenades ?? []), ...thrown],
    structures: state.structures.map(s => (s.id === vehicle.id ? { ...s, smokeUsed: (s.smokeUsed ?? 0) + 1, smokeReady: tick + Math.round(VEHICLE_SMOKE.readySeconds / PHYS_DT) } : s)),
  };
}
